import clsx from "clsx";
import React from "react";

const classes = {
	timeLabelCell: "tw-timeLabelCell",
	timeSlotCell: "tw-timeSlotCell",
	container: "tw-container",
	disabled: "tw-disabled",
	selected: "tw-selected",
};

interface SlotComponentProps {
	className?: string;
	slotValue?: unknown;
	[key: string]: unknown;
}

interface TimeSlotWrapperProps {
	children: React.ReactNode;
	CustomSlotComponent?: React.ComponentType<SlotComponentProps>;
	slotClasses: { root?: string };
	value?: unknown;
	disabled?: boolean;
	selected?: boolean;
	visitAvailability?: boolean;
	timeLabelTextFunc?: (timeLabelText: string) => string;
}

export default function TimeSlotWrapper({
	// children is for first column i.e. time cell
	children,
	CustomSlotComponent,
	slotClasses,
	value,
	disabled,
	selected,
	timeLabelTextFunc,
}: TimeSlotWrapperProps) {
	let slotStatusClassName = selected ? classes.selected : disabled ? classes.disabled : classes.container;

	// Tailwind class mappings (approximate)
	const wrapperClass = "w-full";
	const timeLabelCellClass = "h-full font-thin text-sm text-gray-700 flex items-center justify-start pr-1";
	const timeSlotCellClass = "bg-gray-200/40 rounded-full flex-[0.95] h-[95%] overflow-hidden";
	const timeSlotDisabledCellClass = "bg-gray-200/10 rounded-full flex-[0.95] h-[95%] overflow-hidden";

	// Type guard for React element with props
	const isReactElementWithProps = (
		children: React.ReactNode,
	): children is React.ReactElement<{ className?: string; children: React.ReactElement<{ children: string }> }> => {
		return (
			React.isValidElement(children) &&
			typeof (children.props as { children?: { props?: { children?: unknown } } })?.children?.props?.children ===
				"string"
		);
	};

	return (
		<div className={clsx(wrapperClass, isReactElementWithProps(children) ? children.props.className : "px-2")}>
			{isReactElementWithProps(children) ? (
				timeLabelTextFunc ? (
					<div className={clsx(timeLabelCellClass, children.props.className)}>
						{timeLabelTextFunc(children.props.children.props.children)}
					</div>
				) : (
					<div className={clsx(timeLabelCellClass, children.props.className)}>{children.props.children}</div>
				)
			) : CustomSlotComponent ? (
				<div
					className={
						CustomSlotComponent && slotClasses.root
							? disabled
								? timeSlotDisabledCellClass
								: timeSlotCellClass
							: slotStatusClassName
					}
				>
					<CustomSlotComponent
						className={slotClasses.root ? slotClasses.root : slotStatusClassName}
						slotValue={value}
						data-testid="time-slot-wrapper"
					></CustomSlotComponent>
				</div>
			) : (
				<div className={CustomSlotComponent && slotClasses.root ? timeSlotCellClass : slotStatusClassName}>
					{children}
				</div>
			)}
		</div>
	);
}
